import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import removeInventoryItems from '@/shared/api/inventory/removeInventoryItems';
import updateInventoryAtoms from '@/shared/api/inventory/updateInventoryAtoms';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import { AtomModel_VIEW } from '@/shared/types/atom.type';
import DialogName from '@/shared/types/dialogName.enum';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  inventoryId: string;
  setRefetchMetadata: (value: boolean) => void;
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
  setRefreshTableIndex: Dispatch<SetStateAction<number>>;
};

const useInventoryItemsOperations = ({
  inventoryId,
  setRefetchMetadata,
  onUpdateDialogState,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
  setRefreshTableIndex,
}: Args) => {
  const { t } = useTranslation(['inventory', 'common']);

  const redirectLogin = useRedirectLogin();

  const onUpdateInventoryItems = async (
    metadataDetails: Record<string, unknown>,
    items: AtomModel_VIEW[],
  ) => {
    onUpdateProgressDialog({
      title: t('inventory:updatingInventoryItems'),
      open: true,
      progress: 0,
    });

    const idList = items.map((item) => item.inventory_atom_id);

    const response = await updateInventoryAtoms({
      inventoryId,
      inventoryItemsIdList: idList,
      metadataDetails,
      onUnauthorized: redirectLogin,
      onProgress: (progress) => onUpdateProgressDialog({ progress }),
    });

    if (response.every((item) => item.success)) {
      onUpdateDialogState(DialogName.UpdateInventoryItems, { open: false });
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        message: t('inventory:atomsUpdated'),
        open: true,
      });
      setRefetchMetadata(true);
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        message: t('inventory:atomsNotUpdated'),
        open: true,
      });
      onUpdateDialogState(DialogName.UpdateInventoryItems, {
        errors: response.map((item) => item.msg),
      });
    }
  };

  const onRemoveInventoryItems = async (numberOfAtomsToRemove: number) => {
    onUpdateProgressDialog({
      title: t('inventory:removingInventoryItems'),
      open: true,
      progress: 0,
    });

    const response = await removeInventoryItems({
      id: inventoryId,
      quantity: numberOfAtomsToRemove,
      onUnauthorized: redirectLogin,
      onProgress: (progress) => onUpdateProgressDialog({ progress }),
    });

    if (response.every((item) => item.success)) {
      onUpdateDialogState(DialogName.REMOVE_INVENTORY_ITEMS_CONFIRMATION, {
        open: false,
      });
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        message: t('inventory:inventoryItemsRemoved'),
        open: true,
      });
      setRefetchMetadata(true);
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        message: t('inventory:inventoryItemsNotRemoved'),
        open: true,
      });
      onUpdateDialogState(DialogName.REMOVE_INVENTORY_ITEMS_CONFIRMATION, {
        errors: response.map((item) => item.msg),
      });
    }
  };

  return {
    onUpdateInventoryItems,
    onRemoveInventoryItems,
  };
};

export default useInventoryItemsOperations;
